/**
 * Shared utilities for Tool Audit Log UI: types, formatters, CSV export, and styling constants.
 * Used by ToolAuditLogPopup (client and extension have their own copy for package boundaries).
 */

import type { SxProps, Theme } from "@mui/material/styles";

export interface ToolAuditLogRow {
  id: number;
  /** When returned from ticket endpoint: the thread ID this log belongs to. Not present for single-thread responses. */
  ai_thread_id?: number | string;
  tool_name: string;
  reasoning: string | null;
  param_json: Record<string, unknown> | null;
  output_json: Record<string, unknown> | null;
  error_message: string | null;
  status: string;
  start_timestamp: string | null;
  end_timestamp: string | null;
  user_email?: string | null;
  action_permission?: string | null;
}

export const scrollSx: SxProps<Theme> = {
  "&::-webkit-scrollbar": { width: 10 },
  "&::-webkit-scrollbar-track": { background: "#2d2d2d", borderRadius: 4 },
  "&::-webkit-scrollbar-thumb": { background: "#666", borderRadius: 4 },
  "&::-webkit-scrollbar-thumb:hover": { background: "#888" },
  scrollbarWidth: "thin",
  scrollbarColor: "#666 #2d2d2d",
};

export function formatTimestamp(iso: string | null): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return String(iso);
  }
}

export function formatDuration(startIso: string | null, endIso: string | null): string {
  if (!startIso || !endIso) return "—";
  try {
    const start = new Date(startIso).getTime();
    const end = new Date(endIso).getTime();
    const ms = end - start;
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  } catch {
    return "—";
  }
}

export interface ThreadOption {
  ai_thread_id: number;
  created_at: string | null;
}

/**
 * Format thread created_at similar to AI thread observer. Includes time if multiple threads share the same date.
 */
export function formatThreadTimestamp(created_at: string | null, allThreads: ThreadOption[]): string {
  if (!created_at) return "—";
  try {
    const date = new Date(created_at);
    const now = new Date();
    const oneYearAgo = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
    const dateStr = date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    const sameDateCount = allThreads.filter((t) => {
      if (!t.created_at) return false;
      try {
        const tDate = new Date(t.created_at);
        return tDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) === dateStr;
      } catch {
        return false;
      }
    }).length;
    const includeTime = sameDateCount > 1;
    if (date < oneYearAgo) {
      return includeTime
        ? date.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })
        : date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    }
    return includeTime
      ? date.toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
      : date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    return String(created_at);
  }
}

export function formatParamsAsKeyValue(paramJson: Record<string, unknown> | null): string {
  if (!paramJson || typeof paramJson !== "object") return "";
  return Object.entries(paramJson)
    .map(([k, v]) => {
      const val = v === null || v === undefined ? "null" : typeof v === "object" ? JSON.stringify(v) : String(v);
      return `${k}: ${val}`;
    })
    .join("\n");
}

export function getFullOutputString(outputJson: Record<string, unknown> | null): string {
  if (!outputJson) return "";
  try {
    return JSON.stringify(outputJson, null, 2);
  } catch {
    return String(outputJson);
  }
}

export function isErrorRow(row: ToolAuditLogRow): boolean {
  return String(row.status).toUpperCase() === "ERROR";
}

/** Display type for audit popup: returns action_permission value or "auto" as default. */
export function getActionTypeLabel(row: ToolAuditLogRow): string {
  return row.action_permission ?? "auto";
}

const CSV_HEADERS = ["start_timestamp", "end_timestamp", "user_email", "action_permission", "reasoning", "param_json", "output_json", "error_message", "status"] as const;

function escapeCsvValue(v: unknown): string {
  if (v == null) return "";
  const s = typeof v === "object" ? JSON.stringify(v) : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function getRowValue(row: ToolAuditLogRow, key: string): unknown {
  if (key === "param_json") return row.param_json;
  if (key === "output_json") return row.output_json;
  return (row as unknown as Record<string, unknown>)[key];
}

/**
 * Build CSV content string for the given audit log rows.
 */
export function buildAuditLogCsv(rows: ToolAuditLogRow[]): string {
  const lines = [
    CSV_HEADERS.join(","),
    ...rows.map((r) => CSV_HEADERS.map((h) => escapeCsvValue(getRowValue(r, h))).join(",")),
  ];
  return lines.join("\r\n");
}

/**
 * Trigger download of audit logs as CSV. Caller provides rows and a filename prefix (e.g. ticketId or "export").
 */
export function downloadAuditLogCsv(rows: ToolAuditLogRow[], filenamePrefix: string): void {
  const csv = buildAuditLogCsv(rows);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `audit-logs-${filenamePrefix}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
